import {IronCondorTradeIdeaRecord, isIronCondorIdea, TradeIdeaRecord} from "./TradeIdeaRecord";
import {TradeIdeaRiskViewModel} from "./TradeIdeaRiskViewModel";
import {TradingIdeasConfidenceViewModel} from "./TradingIdeasConfidenceViewModel";
import {Comparator} from "../../types/GenericTypes";
import {formatDate} from "../../js/util/DateTimeUtil";

export type TradeIdeasViewModels = ReadonlyArray<TradeIdeasViewModel>;

export abstract class TradeIdeasViewModel<T extends TradeIdeaRecord = TradeIdeaRecord> {

    protected constructor(public readonly idea: T,
                          public readonly confidence: TradingIdeasConfidenceViewModel,
                          public readonly riskAmount: TradeIdeaRiskViewModel) {
    }

    public abstract updateLots(lots: number): TradeIdeasViewModel<T>;

    public abstract trades(): ReadonlyArray<BaseTrade>;

    public abstract strategyName(): string;

    public get targetPrice(): number | null {
        return this.idea.targetPrice;
    }

    public isSameIdea(updatedModel: TradeIdeasViewModel): boolean {
        return (this.idea.type === updatedModel.idea.type) && (this.idea.underlying === updatedModel.idea.underlying);
    }
}

export class IronCondorTradeIdeasViewModel extends TradeIdeasViewModel<IronCondorTradeIdeaRecord> {

    public constructor(idea: IronCondorTradeIdeaRecord,
                       confidence: TradingIdeasConfidenceViewModel,
                       riskAmount: TradeIdeaRiskViewModel) {
        super(idea, confidence, riskAmount);
    }

    public updateLots(lots: number): IronCondorTradeIdeasViewModel {
        const risk = this.riskAmount.updateLots(lots);
        return new IronCondorTradeIdeasViewModel(this.idea, this.confidence, risk);
    }

    public trades(): ReadonlyArray<BaseTrade> {
        const idea = this.idea;
        const boughtPut: BaseTrade = {
            code: idea.boughtPut.code,
            quantity: 1
        };
        const soldPut: BaseTrade = {
            code: idea.soldPut.code,
            quantity: -1
        };
        const soldCall: BaseTrade = {
            code: idea.soldCall.code,
            quantity: -1
        };
        const boughtCall: BaseTrade = {
            code: idea.boughtCall.code,
            quantity: 1
        };

        return [boughtPut, soldPut, soldCall, boughtCall];
    }

    public strategyName(): string {
        const expiry = formatDate(this.idea.expiry, "DD MMM YY");
        return `${this.idea.underlying} Iron Condor ${expiry}`;
    }
}

export function BuildModel(idea: TradeIdeaRecord,
                           incrementAmount: number = 500,
                           maxRisk: number = 10000): TradeIdeasViewModel {
    const confidence = TradingIdeasConfidenceViewModel.create(idea.confidence);
    const risk = TradeIdeaRiskViewModel.create(idea.risk, incrementAmount, maxRisk);

    if (isIronCondorIdea(idea)) {
        return new IronCondorTradeIdeasViewModel(idea, confidence, risk);
    } else {
        throw Error(`Unsupported Trade Idea Type ${idea.type}`);
    }
}

export function orderByConfidence(): Comparator<TradeIdeasViewModel> {
    return (a, b) => b.confidence.confidence - a.confidence.confidence;
}
